<template>
    <div>
        <div class="kgr-row">
            <h3 class="kgr-headline kgr-headline--h3">
                {{ $t('Mandatsreferenzen') }}
            </h3>
        </div>
        <div class="kgr-row">
            <div class="kgr-grid kgr-grid--gutters kgr-grid--bigGutters">
                <div class="kgr-grid__cell">
                    <kgr-checkbox
                        v-model="filter.showInactive"
                        :label="$t('Inaktive Mandatsreferenzen anzeigen')"
                    />
                </div>
                <div class="kgr-grid__cell">
                    <button
                        class="kgr-button kgr-button--accent kgr-float kgr-float--right"
                        @click="openCreateDialog()"
                    >
                        {{ $t('Neue Mandatsreferenz anlegen') }}
                    </button>
                </div>
            </div>
        </div>
        <table class="kgr-table kgr-table--block kgr-table--striped printable">
            <thead>
                <tr class="kgr-table__row">
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Mandatsreferenznummer') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                        {{ $t('Ausstellungsdatum') }}
                    </th>
                    <th class="kgr-table__cell kgr-table__cell--headline">
                    </th>
                </tr>
            </thead>
            <tbody>
                <tr v-if="loading">
                    <td
                        colspan="3"
                        style="text-align: center;"
                    >
                        <kgr-loader />
                    </td>
                </tr>
                <tr v-else-if="filteredMandateReferences.length === 0">
                    <td
                        colspan="3"
                        style="text-align: center;"
                    >
                        {{ $t('dataTable.noData') }}
                    </td>
                </tr>
                <template v-else>
                    <tr
                        v-for="mandateReference in filteredMandateReferences"
                        :key="mandateReference.id"
                        class="kgr-table__row"
                    >
                        <td class="kgr-table__cell">
                            {{ mandateReference.number }}
                        </td>
                        <td class="kgr-table__cell">
                            {{ mandateReference.grantedAt }}
                        </td>
                        <td class="kgr-table__cell">
                            <button
                                class="kgr-table__action"
                                :disabled="!mandateReference.isDeletable"
                                @click="deleteMandateReference(mandateReference.id)"
                            >
                                <kgr-icon
                                    icon-class="delete"
                                    :disabled="!mandateReference.isDeletable"
                                />
                            </button>
                            <button
                                class="kgr-table__action"
                                :disabled="mandateReference.isDeletable || mandateReference.isArchived"
                                @click="archiveMandateReference(mandateReference.id)"
                            >
                                <kgr-icon
                                    icon-class="archive"
                                    :disabled="mandateReference.isDeletable || mandateReference.isArchived"
                                />
                            </button>
                        </td>
                    </tr>
                </template>
            </tbody>
        </table>
        <kgr-lightbox
            v-if="newMandateReference"
            :value="showDialog"
            :headline="$t('Mandatsreferenznummer anlegen')"
            animated
            @post-input="closeModal"
        >
            <div>
                <kgr-generic-radio
                    v-model="autoCreate"
                    :options="autoCreateOptions"
                />
                <kgr-form-row
                    v-if="!autoCreate"
                    :label="$t('Mandatsreferenznummer')"
                    :error="error"
                    :tooltip="$t('Die Mandatsreferenznummer darf nur Buchstaben, Zahlen und die Zeichen .,\':()-+/? enthalten und muss zwischen 1 und 35 Zeichen lang sein')"
                    field-name="number"
                >
                    <kgr-text-input
                        v-model="newMandateReference.number"
                        :is-invalid="!isValidNumber"
                        :max-length="35"
                    />
                </kgr-form-row>
                <kgr-form-row
                    :label="$t('Ausstellungsdatum')"
                    :error="error"
                    field-name="grantedAt"
                >
                    <kgr-date-picker
                        v-model="newMandateReference.grantedAt"
                    />
                </kgr-form-row>
                <hr class="kgr-separator--light" />
                <div class="kgr-row">
                    <button
                        class="kgr-button"
                        @click="closeModal"
                    >
                        {{ $t('cancel') }}
                    </button>
                    <button
                        class="kgr-button kgr-button--accent"
                        @click="createMandateReference"
                    >
                        {{ $t('save') }}
                    </button>
                </div>
            </div>
        </kgr-lightbox>
    </div>
</template>

<script>
import Checkbox from '../../../components/common/form/checkbox/Checkbox.vue';
import DatePicker from '../../../components/common/form/picker/DatePicker.vue';
import Error from '../../../js/model/Error.js';
import FormRow from '../../../components/common/form/FormRow.vue';
import GenericRadio from '../../../components/common/form/radio/GenericRadio.vue';
import Icon from '../../../components/common/icon/Icon.vue';
import Lightbox from '../../../components/common/modal/Lightbox.vue';
import Loading from '../../../components/common/Loading.vue';
import TextInput from '../../../components/common/form/text-input/TextInput.vue';
import axios from 'axios';
import { format } from '@legacy/js/date/date-fns.js';

export default {
    components: {
        'kgr-form-row': FormRow,
        'kgr-date-picker': DatePicker,
        'kgr-lightbox': Lightbox,
        'kgr-loader': Loading,
        'kgr-checkbox': Checkbox,
        'kgr-icon': Icon,
        'kgr-text-input': TextInput,
        'kgr-generic-radio': GenericRadio,
    },
    props: {
        memberId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            showDialog: false,
            valid: false,
            autoCreate: true,
            autoCreateOptions: {
                'Mandatsreferenznummer automatisch generieren': true,
                'Manuelle Eingabe': false,
            },
            loading: false,
            initialLoading: true,
            mandateReferences: [],
            newMandateReference: null,
            filter: {
                showInactive: false,
            },
        };
    },
    computed: {
        isValidNumber() {
            if (!this.newMandateReference?.number) {
                return true;
            }
            return /^[A-Za-z0-9+?/:().,'-]{1,35}$/.test(this.newMandateReference.number);
        },
        filterText() {
            return this.filter.showInactive ? this.$t('Alle Mandatsreferenzen werden angezeigt') : this.$t('Archivierte Mandatsreferenzen sind ausgeblendet');
        },
        filteredMandateReferences() {
            if (!this.filter.showInactive) {
                return this.mandateReferences.filter((mandateReference) => !mandateReference.isArchived);
            }
            return this.mandateReferences;
        },
    },
    created() {
        this.loadMandateReferences();
    },
    methods: {
        loadMandateReferences() {
            this.loading = true;
            axios
                .get(`/club/members/${this.memberId}/mandate-references`)
                .then((response) => {
                    this.mandateReferences = response.data.data;
                    this.loading = false;
                    this.initialLoading = false;
                });
        },
        createMandateReference() {
            axios
                .post(`/club/members/${this.memberId}/mandate-references`, {
                    autoCreate: this.autoCreate,
                    number: this.newMandateReference.number,
                    grantedAt: this.getApiDateFromString(this.newMandateReference.grantedAt),
                })
                .then(() => {
                    this.closeModal();
                    this.loadMandateReferences();
                    this.$emit('save');
                })
                .catch((error) => this.error = Error.fromResponse(error.response));
        },
        archiveMandateReference(id) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du die Mandatsreferenz wirklich archivieren?'), () => {
                axios
                    .put(`/club/mandate-references/${id}/archive`)
                    .then(() => {
                        this.loadMandateReferences();
                        this.$emit('save');
                    })
                    .catch(() => this.loading = false);
            }, this.$t('Archivieren'));
        },
        deleteMandateReference(id) {
            showConfirm(this.$t('confirmInformal'), this.$t('Willst du die Mandatsreferenz wirklich löschen?'), () => {
                axios
                    .delete(`/club/mandate-references/${id}`)
                    .then(() => {
                        this.loadMandateReferences();
                        this.$emit('save');
                    })
                    .catch(() => this.loading = false);
            }, this.$t('Löschen'));
        },
        initNew() {
            this.newMandateReference = {
                number: '',
                grantedAt: format(new Date(), 'dd.MM.yyyy'),
            };
        },
        getApiDateFromString(string) {
            if (string === null) {
                return null;
            }

            const [day, month, year] = string.split('.');

            return {
                day: Number.parseInt(day),
                month: Number.parseInt(month),
                year: Number.parseInt(year),
            };
        },
        openCreateDialog() {
            this.initNew();
            this.showDialog = true;
        },
        closeModal() {
            this.showDialog = false;
        },
    },
};
</script>
